import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'

export interface MicrositeTheme {
  primary_color?: string
  secondary_color?: string
  accent_color?: string
  button_color?: string
  button_text_color?: string
  header_bg_color?: string
  header_text_color?: string
  footer_bg_color?: string
  footer_text_color?: string
  font_family?: string
  heading_font_family?: string
  logo_url?: string
  hero_image_url?: string
  favicon_url?: string
  footer_text?: string
  contact_phone?: string
  contact_email?: string
  contact_address?: string
  og_title?: string
  og_description?: string
  og_image?: string
  meta_description?: string
  gtm_id?: string
  ga4_id?: string
  clarity_id?: string
}

export interface Microsite {
  microsite_slug: string | null
  microsite_template: 'modern' | 'classic' | 'elegant' | null
  microsite_theme: MicrositeTheme | null
  microsite_published_at: string | null
  microsite_publish_status: 'publishing' | 'published' | 'failed' | null
  microsite_publish_error: string | null
  pages_count: number
}

export type MicrositePageType = 'home' | 'about' | 'contact' | 'rooms' | 'room' | 'custom'

export type ButtonActionType = 'link' | 'page' | 'phone' | 'email' | 'line'

export interface ButtonAction {
  text: string
  action_type: ButtonActionType
  url?: string
  slug?: string
  phone?: string
  email?: string
  line_id?: string
}

export interface HomeRoomItem {
  name: string
  price: string
  size_sqm: string
  image?: string
  cta?: ButtonAction
}

export interface HomePageContent {
  hero_title?: string
  hero_subtitle?: string
  hero_cta?: ButtonAction
  rooms?: HomeRoomItem[]
}

export interface RoomPageContent {
  room_name?: string
  price?: string
  size_sqm?: string
  description?: string
  amenities?: string[]
  images?: string[]
  cta?: ButtonAction
  available_rooms?: HomeRoomItem[]
}

export interface ContactPageContent {
  heading?: string
  description?: string
  phone?: string
  email?: string
  address?: string
  google_maps_embed_url?: string
  cta?: ButtonAction
}

export type MicrositePageContent = HomePageContent | RoomPageContent | ContactPageContent | Record<string, unknown>

export interface MicrositePage {
  id: number
  property_id: number
  title: string
  slug: string
  body: string | null
  content: MicrositePageContent
  page_type: MicrositePageType
  sort_order: number
  is_published: boolean
  show_in_nav: boolean
  created_at: string
  updated_at: string
}

export interface UpdateMicrositeInput {
  microsite_slug?: string
  microsite_template?: string
  microsite_theme?: MicrositeTheme
}

export interface CreateMicrositePageInput {
  title: string
  slug: string
  body?: string
  content?: MicrositePageContent
  page_type?: MicrositePageType
  sort_order?: number
  is_published?: boolean
  show_in_nav?: boolean
}

export interface UpdateMicrositePageInput {
  id: number
  title?: string
  slug?: string
  body?: string
  content?: MicrositePageContent
  page_type?: MicrositePageType
  sort_order?: number
  is_published?: boolean
  show_in_nav?: boolean
}

export const micrositeKeys = {
  all: (propertyId: number) => ['microsite', propertyId] as const,
  detail: (propertyId: number) => [...micrositeKeys.all(propertyId), 'detail'] as const,
  pages: (propertyId: number) => [...micrositeKeys.all(propertyId), 'pages'] as const,
}

export function useMicrosite(propertyId: number) {
  return useQuery({
    queryKey: micrositeKeys.detail(propertyId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Microsite>>(
        `/api/properties/${propertyId}/microsite`
      )
      return data.data
    },
    enabled: !!propertyId,
    refetchInterval: (query) =>
      query.state.data?.microsite_publish_status === 'publishing' ? 3000 : false,
  })
}

export function useUpdateMicrosite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      propertyId,
      ...input
    }: UpdateMicrositeInput & { propertyId: number }) => {
      const { data } = await api.patch<ApiResponse<Microsite>>(
        `/api/properties/${propertyId}/microsite`,
        input
      )
      return data.data
    },
    onSuccess: (_data, variables) => {
      // Use removeListQueries (eviction) instead of invalidateDetailQueries to avoid a double-fetch
      // with the polling observer (refetchInterval: 3000ms when status === 'publishing').
      // The observer re-fetches on its next interval, populating the cache without an immediate refetch.
      removeListQueries(queryClient, micrositeKeys.detail(variables.propertyId))
    },
  })
}

export function usePublishMicrosite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (propertyId: number) => {
      const { data } = await api.post<ApiResponse<Microsite>>(
        `/api/properties/${propertyId}/microsite/publish`
      )
      return data.data
    },
    onSuccess: (_data, propertyId) => {
      // Use removeListQueries (eviction) instead of invalidateDetailQueries to avoid a double-fetch
      // with the polling observer (refetchInterval: 3000ms when status === 'publishing').
      // The observer re-fetches on its next interval, populating the cache without an immediate refetch.
      removeListQueries(queryClient, micrositeKeys.detail(propertyId))
    },
  })
}

export function useMicrositePages(propertyId: number) {
  return useQuery({
    queryKey: micrositeKeys.pages(propertyId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<MicrositePage[]>>(
        `/api/properties/${propertyId}/microsite_pages`
      )
      return data.data
    },
    enabled: !!propertyId,
  })
}

export function useCreateMicrositePage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      propertyId,
      ...input
    }: CreateMicrositePageInput & { propertyId: number }) => {
      const { data } = await api.post<ApiResponse<MicrositePage>>(
        `/api/properties/${propertyId}/microsite_pages`,
        { microsite_page: input }
      )
      return data.data
    },
    onSuccess: (_data, variables) => {
      removeListQueries(queryClient, micrositeKeys.pages(variables.propertyId))
    },
  })
}

export function useUpdateMicrositePage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      propertyId,
      id,
      ...input
    }: UpdateMicrositePageInput & { propertyId: number }) => {
      const { data } = await api.patch<ApiResponse<MicrositePage>>(
        `/api/properties/${propertyId}/microsite_pages/${id}`,
        { microsite_page: input }
      )
      return data.data
    },
    onSuccess: (_data, variables) => {
      removeListQueries(queryClient, micrositeKeys.pages(variables.propertyId))
    },
  })
}

export function useDeleteMicrositePage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ propertyId, id }: { propertyId: number; id: number }) => {
      await api.delete(`/api/properties/${propertyId}/microsite_pages/${id}`)
      return id
    },
    onSuccess: (_data, variables) => {
      removeListQueries(queryClient, micrositeKeys.pages(variables.propertyId))
    },
  })
}
